import { useState, useEffect, type FC, type MouseEvent } from 'react'
import classnames from 'classnames'
import { observer } from 'mobx-react-lite'
import { IconButton, Typography, Badge } from 'shared/ui'
import { useOutsideAlerter } from 'shared/hooks'
import { NAME_INBOX } from 'entities/Inbox'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type GroupInbox } from 'entities/Inbox/model/GroupInbox'
import { DropdownGroupInbox } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/Dropdown'
import { useConversationMenuContext } from 'pages/conversations/ui/ConversationMenu/context'
import { MenuCardInput } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/MenuCard/MenuCardInput/MenuCardInput'
import { useEventInboxMemberRemoved } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/MenuCard/MenuCardInboxGroup/events/EventInboxMemberRemoved'
import styles from './styles.module.scss'

type IMenuCardInboxGroupProps = {
  item: GroupInbox
  onToggle: () => void
  isOpen: boolean
  hasChild?: boolean
  droppable?: boolean
  onClickItemTree?: () => void
}

export const MenuCardInboxGroup: FC<IMenuCardInboxGroupProps> = observer(
  ({ item: groupInbox, onToggle, isOpen, droppable, hasChild, onClickItemTree }) => {
    const store = useConversationMenuContext()
    const { inboxesStore } = useEntitiesContext()
    const {
      loadingUpdateTeam,
      handleUpdateTeamInbox,
      deleteItemTree,
      disabledActive,
      resetCurrentConversation,
      config,
      isActiveConversationSearch,
    } = store
    const { currentInboxId, groupInboxStore } = inboxesStore
    const { renameGroup, toggleRenameMode } = groupInboxStore
    const active = currentInboxId === groupInbox.id
    const [hover, setHover] = useState(false)
    const [name, setName] = useState(groupInbox.name)
    const handleMouseEnter = () => setHover(true)
    const handleMouseLeave = () => setHover(false)
    const handleToggle = (e: MouseEvent) => {
      e.stopPropagation()

      if (droppable) {
        onToggle()
      }
    }

    useEventInboxMemberRemoved(store, groupInbox)

    useEffect(() => {
      window.addEventListener('keyup', onKeyUp)

      return () => {
        window.removeEventListener('keyup', onKeyUp)
      }
    }, [])

    if (groupInbox.type !== 'inbox_group') return null

    const totalUnread = groupInbox.teamsIds.reduce<number>((state, id) => {
      const inbox = inboxesStore.getItem(id)

      if (inbox) {
        state = state + inbox.unreadCount
      }

      return state
    }, 0)

    const handleClickUpdateTeam = async () => {
      if (groupInbox.is_rename) return
      if (loadingUpdateTeam) return

      if (isActiveConversationSearch) {
        resetCurrentConversation()
        config?.resetConversations?.()
        config?.setConversationsLoading?.(true)
      }

      inboxesStore.localInboxes.resetCurrentInbox()
      deleteItemTree(NAME_INBOX.Create)
      await handleUpdateTeamInbox(groupInbox.id)
      onClickItemTree?.()
    }
    const { ref } = useOutsideAlerter(() => {
      toggleRenameMode(groupInbox, false)
      setName(groupInbox.name)
    })
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        toggleRenameMode(groupInbox, false)
        setName(groupInbox.name)
      }
    }

    const renderButton = () => {
      if (hasChild && hover) {
        return (
          <IconButton
            icon={isOpen ? 'chevronDown' : 'chevronRight'}
            size={'small'}
            color={'secondary'}
            ariaLabel='MenuCardInboxGroup_hasChild_hover'
          />
        )
      }

      return (
        <IconButton
          icon={groupInbox.icon}
          iconProps={{
            color: active && !disabledActive ? 'var(--content-brand-primary)' : undefined,
          }}
          size={'small'}
          color={'transparent'}
          fontSize={16}
          ariaLabel='MenuCardInboxGroup_noChild'
        />
      )
    }

    return (
      <div ref={ref} className={styles.wrap}>
        <div
          className={classnames(styles.wrapGroup, {
            [styles.active]: active && !disabledActive,
            [styles.disabled]: loadingUpdateTeam,
          })}
          onClick={handleClickUpdateTeam}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {groupInbox.is_rename && (
            <div className={classnames(styles.boxName, styles.boxName__input)}>
              <div className={styles.icon}>
                <IconButton
                  icon={groupInbox.icon}
                  size={'small'}
                  color={'transparent'}
                  fontSize={16}
                  ariaLabel='MenuCardInboxGroup_isRename'
                />
              </div>

              <MenuCardInput
                value={name}
                onChange={(e) => {
                  const target = e.target

                  if (target instanceof HTMLInputElement) {
                    setName(target.value)
                  }
                }}
                onKeyUp={(e) => {
                  if (e.key === 'Enter' && name.length) {
                    renameGroup(name, groupInbox)
                  }
                }}
              />
            </div>
          )}

          {!groupInbox.is_rename && (
            <>
              <div className={styles.boxName}>
                <div className={styles.icon} onClick={handleToggle}>
                  {renderButton()}
                </div>
                <Typography
                  variant={'body-md-regular'}
                  ellipsis
                  color={'var(--content-primary-primary)'}
                  ariaLabel={'name'}
                >
                  {groupInbox.name}
                </Typography>
              </div>

              {Boolean(totalUnread) && (
                <div className={styles.infoItem}>
                  <Badge
                    content={totalUnread}
                    ariaLabel={'MenuCardInboxGroup_unreadMessagesCount'}
                  />
                </div>
              )}
              <div className={styles.boxDropdownMenu}>
                <DropdownGroupInbox inbox={groupInbox} />
              </div>
            </>
          )}
        </div>
      </div>
    )
  }
)
